/**
 * CannedResponseEditModal — Task 9: criar/editar uma Resposta Pronta da biblioteca.
 *
 * Título/categoria/atalho + builder de items (texto = textarea; mídia = seletor
 * de `useMediaAssets` + upload inline via `useUploadMediaAsset`, erro honesto do
 * Engine — MIME/tamanho) + reordenação por botões ↑/↓ (drag-drop é YAGNI v1,
 * decisão do controller) + checkboxes de playbooks vinculados.
 *
 * Save: create (POST) OU update (PATCH If-Match); quando a seleção de playbooks
 * muda, encadeia `setCannedResponsePlaybooks` com a version resultante do
 * create/update (nunca a version pré-save — evitaria 409 espúrio). 409 no
 * create/update → "editado em outra sessão, recarregue" (não sobrescreve cego,
 * ADR-071).
 *
 * Mirror estrutural de `ServiceFormModal` (scheduling-resources): dialog
 * próprio (não o `Modal` genérico — precisamos desabilitar o Salvar por
 * validação, o `Modal` só desabilita via `requireConfirmText` destrutivo).
 */
import { ArrowDown, ArrowUp, Image as ImageIcon, Plus, Trash2, X } from 'lucide-react';
import { useState } from 'react';

import {
  useCreateCannedResponse,
  useMediaAssets,
  useSetCannedResponsePlaybooks,
  useUpdateCannedResponse,
  useUploadMediaAsset,
} from '../../hooks/use-canned-responses';
import {
  ApiError,
  type CannedResponseItemWire,
  type CannedResponseWire,
} from '../../lib/api-client';
import { usePlaybookList } from '../playbooks/use-playbooks';

import { useMediaAssetObjectUrl } from './use-media-asset-object-url';

import type { JSX } from 'react';

export interface CannedResponseEditModalProps {
  mode: 'create' | 'edit';
  response?: CannedResponseWire;
  onClose: () => void;
}

function sameIdSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const setB = new Set(b);
  return a.every((id) => setB.has(id));
}

function isItemValid(item: CannedResponseItemWire): boolean {
  return item.kind === 'text' ? item.text.trim() !== '' : item.mediaAssetId !== '';
}

export function CannedResponseEditModal({
  mode,
  response,
  onClose,
}: CannedResponseEditModalProps): JSX.Element {
  const [title, setTitle] = useState(response?.title ?? '');
  const [category, setCategory] = useState(response?.category ?? '');
  const [shortcut, setShortcut] = useState(response?.shortcut ?? '');
  const [items, setItems] = useState<CannedResponseItemWire[]>(response?.items ?? []);
  const [playbookIds, setPlaybookIds] = useState<string[]>(response?.playbookIds ?? []);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  /**
   * Identidade+version FRESCAS do recurso salvo — atualizadas após CADA
   * create/update bem-sucedido (objeto retornado pela mutation), nunca a
   * version do mount. Sem isso, um save parcial (conteúdo ok → vínculo de
   * playbooks falha) deixaria o retry re-enviando o update com a version
   * ORIGINAL → loop de 409 espúrio até fechar/reabrir o modal. Também
   * promove o modo create→update após o primeiro create (retry não duplica).
   */
  const [saved, setSaved] = useState<{ id: string; version: number } | null>(
    response !== undefined ? { id: response.id, version: response.version } : null,
  );
  /** Baseline dos vínculos já persistidos (p/ pular o PUT quando nada mudou). */
  const [linkedPlaybookIds, setLinkedPlaybookIds] = useState<string[]>(response?.playbookIds ?? []);

  const playbooks = usePlaybookList();
  const createMut = useCreateCannedResponse();
  const updateMut = useUpdateCannedResponse();
  const setPlaybooksMut = useSetCannedResponsePlaybooks();

  const valid = title.trim() !== '' && items.length > 0 && items.every(isItemValid);
  const busy = createMut.isPending || updateMut.isPending || setPlaybooksMut.isPending;

  const addTextItem = (): void => {
    setItems((prev) => [...prev, { id: crypto.randomUUID(), kind: 'text', text: '' }]);
  };
  const addMediaItem = (): void => {
    setItems((prev) => [...prev, { id: crypto.randomUUID(), kind: 'media', mediaAssetId: '' }]);
  };
  const removeItem = (index: number): void => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };
  const moveItem = (index: number, direction: -1 | 1): void => {
    setItems((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(index, 1);
      next.splice(target, 0, moved!);
      return next;
    });
  };
  const updateTextItem = (index: number, text: string): void => {
    setItems((prev) =>
      prev.map((it, i) => (i === index && it.kind === 'text' ? { ...it, text } : it)),
    );
  };
  const updateMediaItem = (
    index: number,
    patch: { mediaAssetId?: string; caption?: string },
  ): void => {
    setItems((prev) =>
      prev.map((it, i) => (i === index && it.kind === 'media' ? { ...it, ...patch } : it)),
    );
  };
  const togglePlaybook = (id: string): void => {
    setPlaybookIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  // M1 — 409 de atalho duplicado (unique tenantId+shortcut) é um conflito
  // DIFERENTE do 409 de version If-Match desatualizada; a copy genérica
  // "editada em outra sessão" é enganosa para o primeiro caso. `ApiError`
  // já carrega a mensagem do Engine em `.message` (ConflictException do
  // `canned-responses.service.ts`) — surfamos ela quando reconhecível como
  // conflito de atalho; senão mantemos a copy genérica de versão/sessão.
  const handle409 = (err: unknown): boolean => {
    if (err instanceof ApiError && err.status === 409) {
      if (err.message.includes('atalho')) {
        setErrorMessage(err.message);
      } else {
        setErrorMessage(
          'Esta resposta foi editada em outra sessão — recarregue e tente novamente.',
        );
      }
      return true;
    }
    return false;
  };

  const finishWithPlaybooks = (id: string, version: number): void => {
    if (sameIdSet(playbookIds, linkedPlaybookIds)) {
      onClose();
      return;
    }
    setPlaybooksMut.mutate(
      { id, version, playbookIds },
      {
        onSuccess: (result) => {
          // PUT também versiona o recurso — manter a version fresca.
          setSaved({ id: result.id, version: result.version });
          setLinkedPlaybookIds(playbookIds);
          onClose();
        },
        onError: (err) => {
          // O CONTEÚDO foi salvo (a version fresca já está em `saved`) — só o
          // vínculo falhou. Mensagem honesta (não é "editado em outra sessão"
          // do save de conteúdo); o próximo Salvar re-tenta só o vínculo.
          if (err instanceof ApiError && err.status === 409) {
            setErrorMessage(
              'Conteúdo salvo, mas os playbooks vinculados mudaram em outra sessão — clique em Salvar para tentar de novo.',
            );
            return;
          }
          setErrorMessage(
            'Conteúdo salvo, mas não foi possível atualizar os playbooks vinculados — clique em Salvar para tentar de novo.',
          );
        },
      },
    );
  };

  const handleSubmit = (): void => {
    if (!valid || busy) return;
    setErrorMessage(null);
    const body = {
      title: title.trim(),
      items,
      ...(category.trim() !== '' && { category: category.trim() }),
      ...(shortcut.trim() !== '' && { shortcut: shortcut.trim() }),
    };

    // `saved === null` só no create ainda não persistido; depois do primeiro
    // create bem-sucedido o retry cai no PATCH (não re-POSTa duplicado).
    if (saved === null) {
      createMut.mutate(body, {
        onSuccess: (created) => {
          setSaved({ id: created.id, version: created.version });
          finishWithPlaybooks(created.id, created.version);
        },
        onError: (err) => {
          if (!handle409(err)) setErrorMessage('Não foi possível criar a resposta pronta.');
        },
      });
      return;
    }

    updateMut.mutate(
      { id: saved.id, version: saved.version, body },
      {
        onSuccess: (updated) => {
          setSaved({ id: updated.id, version: updated.version });
          finishWithPlaybooks(updated.id, updated.version);
        },
        onError: (err) => {
          if (!handle409(err)) setErrorMessage('Não foi possível salvar as alterações.');
        },
      },
    );
  };

  return (
    <div
      role="dialog"
      aria-label={mode === 'create' ? 'Nova resposta' : `Editar ${response?.title ?? 'resposta'}`}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div
        className="flex w-full max-w-lg flex-col rounded-lg p-6 shadow-xl"
        style={{ background: 'var(--bg-surface)', maxHeight: '90vh', overflowY: 'auto' }}
      >
        <div className="mb-1 flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            {mode === 'create' ? 'Nova resposta' : 'Editar resposta'}
          </h2>
          <button type="button" className="btn btn--ghost btn--sm" onClick={onClose}>
            <X size={14} aria-hidden /> Fechar
          </button>
        </div>

        {errorMessage !== null && (
          <p
            className="mb-3 rounded-md px-3 py-2 text-sm"
            role="alert"
            style={{ background: 'var(--danger-bg)', color: 'var(--danger-fg)' }}
          >
            {errorMessage}
          </p>
        )}

        <label className="mb-3 block">
          <span className="mb-1 block text-sm font-medium">Título</span>
          <input
            className="w-full rounded border px-2 py-1"
            style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={120}
            placeholder="Ex.: Horário de atendimento"
            aria-label="Título"
          />
        </label>

        <div className="mb-3 grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 block text-sm font-medium">Categoria (opcional)</span>
            <input
              className="w-full rounded border px-2 py-1"
              style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              maxLength={60}
              aria-label="Categoria"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium">Atalho (opcional)</span>
            <input
              className="w-full rounded border px-2 py-1"
              style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
              value={shortcut}
              onChange={(e) => setShortcut(e.target.value)}
              maxLength={32}
              placeholder="ex.: horario"
              aria-label="Atalho"
            />
          </label>
        </div>

        <div className="mb-3 flex items-center justify-between">
          <span className="text-sm font-medium">Itens da resposta</span>
          <div className="flex gap-2">
            <button type="button" className="btn btn--ghost btn--sm" onClick={addTextItem}>
              <Plus size={13} aria-hidden /> Texto
            </button>
            <button type="button" className="btn btn--ghost btn--sm" onClick={addMediaItem}>
              <ImageIcon size={13} aria-hidden /> Mídia
            </button>
          </div>
        </div>

        {items.length === 0 ? (
          <p className="mb-4 text-sm" style={{ color: 'var(--fg-3)' }}>
            Nenhum item ainda — adicione texto ou mídia acima.
          </p>
        ) : (
          <div className="mb-4 flex flex-col gap-2">
            {items.map((item, index) => (
              <ItemEditor
                key={item.id}
                item={item}
                index={index}
                total={items.length}
                onRemove={() => removeItem(index)}
                onMove={(dir) => moveItem(index, dir)}
                onUpdateText={(text) => updateTextItem(index, text)}
                onUpdateMedia={(patch) => updateMediaItem(index, patch)}
              />
            ))}
          </div>
        )}

        <fieldset className="mb-4">
          <legend className="mb-1 block text-sm font-medium">
            Playbooks vinculados (opcional)
          </legend>
          {playbooks.data.length === 0 ? (
            <p className="text-xs" style={{ color: 'var(--fg-3)' }}>
              Nenhum playbook disponível.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {playbooks.data.map((pb) => (
                <label
                  key={pb.id}
                  className="flex cursor-pointer items-center gap-1 rounded border px-2 py-1 text-sm"
                  style={{
                    borderColor: playbookIds.includes(pb.id)
                      ? 'var(--brand)'
                      : 'var(--border-default)',
                    background: playbookIds.includes(pb.id)
                      ? 'var(--brand-subtle)'
                      : 'var(--bg-surface-2)',
                    color: playbookIds.includes(pb.id) ? 'var(--brand-subtle-fg)' : 'var(--fg-2)',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={playbookIds.includes(pb.id)}
                    onChange={() => togglePlaybook(pb.id)}
                    aria-label={`Vincular ao playbook ${pb.name}`}
                  />
                  {pb.name}
                </label>
              ))}
            </div>
          )}
        </fieldset>

        <button
          type="button"
          className="btn btn--primary w-full"
          onClick={handleSubmit}
          disabled={!valid || busy}
        >
          {/* `saved`-driven (não `mode`): após um create parcial (conteúdo ok,
              vínculo falhou) o retry é PATCH — o rótulo acompanha. */}
          {busy ? 'Salvando…' : saved === null ? 'Criar resposta' : 'Salvar alterações'}
        </button>
      </div>
    </div>
  );
}

interface ItemEditorProps {
  item: CannedResponseItemWire;
  index: number;
  total: number;
  onRemove: () => void;
  onMove: (direction: -1 | 1) => void;
  onUpdateText: (text: string) => void;
  onUpdateMedia: (patch: { mediaAssetId?: string; caption?: string }) => void;
}

function ItemEditor({
  item,
  index,
  total,
  onRemove,
  onMove,
  onUpdateText,
  onUpdateMedia,
}: ItemEditorProps): JSX.Element {
  return (
    <div className="rounded-md p-3" style={{ border: '1px solid var(--border-default)' }}>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold" style={{ color: 'var(--fg-3)' }}>
          #{index + 1} · {item.kind === 'text' ? 'Texto' : 'Mídia'}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            aria-label={`Mover item ${index + 1} para cima`}
            disabled={index === 0}
            onClick={() => onMove(-1)}
          >
            <ArrowUp size={13} aria-hidden />
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            aria-label={`Mover item ${index + 1} para baixo`}
            disabled={index === total - 1}
            onClick={() => onMove(1)}
          >
            <ArrowDown size={13} aria-hidden />
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            aria-label={`Remover item ${index + 1}`}
            onClick={onRemove}
          >
            <Trash2 size={13} aria-hidden />
          </button>
        </div>
      </div>
      {item.kind === 'text' ? (
        <textarea
          className="w-full rounded border px-2 py-1"
          style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
          rows={2}
          value={item.text}
          onChange={(e) => onUpdateText(e.target.value)}
          aria-label={`Texto do item ${index + 1}`}
        />
      ) : (
        <MediaItemFields
          mediaAssetId={item.mediaAssetId}
          caption={item.caption ?? ''}
          index={index}
          onUpdate={onUpdateMedia}
        />
      )}
    </div>
  );
}

function MediaItemFields({
  mediaAssetId,
  caption,
  index,
  onUpdate,
}: {
  mediaAssetId: string;
  caption: string;
  index: number;
  onUpdate: (patch: { mediaAssetId?: string; caption?: string }) => void;
}): JSX.Element {
  const mediaQ = useMediaAssets(true);
  const uploadMut = useUploadMediaAsset();
  const [uploadError, setUploadError] = useState<string | null>(null);
  const preview = useMediaAssetObjectUrl(mediaAssetId !== '' ? mediaAssetId : null);
  const selectedAsset = mediaQ.data?.data.find((m) => m.id === mediaAssetId);

  const handleFile = (file: File | undefined): void => {
    if (file === undefined) return;
    setUploadError(null);
    uploadMut.mutate(file, {
      onSuccess: (asset) => {
        onUpdate({ mediaAssetId: asset.id });
      },
      onError: (err) => {
        setUploadError(err instanceof ApiError ? err.message : 'Falha no upload.');
      },
    });
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <select
          className="flex-1 rounded border px-2 py-1"
          style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
          value={mediaAssetId}
          onChange={(e) => onUpdate({ mediaAssetId: e.target.value })}
          aria-label={`Mídia do item ${index + 1}`}
        >
          <option value="">Selecione um arquivo…</option>
          {(mediaQ.data?.data ?? []).map((asset) => (
            <option key={asset.id} value={asset.id}>
              {asset.filename}
            </option>
          ))}
        </select>
        {selectedAsset?.kind === 'image' && preview.url !== null && (
          <img
            src={preview.url}
            alt={selectedAsset.filename}
            style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4 }}
          />
        )}
      </div>
      <label className="text-xs" style={{ color: 'var(--fg-3)' }}>
        <span className="mr-2">ou envie um arquivo novo:</span>
        <input
          type="file"
          aria-label={`Enviar mídia para o item ${index + 1}`}
          onChange={(e) => handleFile(e.target.files?.[0])}
          disabled={uploadMut.isPending}
        />
      </label>
      {uploadMut.isPending && (
        <span className="text-xs" style={{ color: 'var(--fg-3)' }}>
          Enviando…
        </span>
      )}
      {uploadError !== null && (
        <span className="text-xs" role="alert" style={{ color: 'var(--danger-fg)' }}>
          {uploadError}
        </span>
      )}
      <input
        type="text"
        className="rounded border px-2 py-1 text-sm"
        style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
        value={caption}
        onChange={(e) => onUpdate({ caption: e.target.value })}
        placeholder="Legenda (opcional)"
        aria-label={`Legenda do item ${index + 1}`}
      />
    </div>
  );
}
